import { useMemo } from "react";
import { Link } from "wouter";
import {
  ChevronLeft,
  Activity,
  Boxes,
  Play,
  Trophy,
  Target,
  Shield,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useGames, useOrgScoringInsights } from "@/lib/queries";
import type { Game, OrgScoringInsights } from "@/lib/types";

// Org-wide pulse. Sits one layer above the per-player profile —
// these charts answer "what's happening across the whole org"
// rather than "how is X doing". All client-computed from the
// useGames() roster since we already pull it on the home page
// and React Query memoizes — no new endpoint needed for v1.
//
// Sections:
//   1. Activity timeline — stacked weekly bar chart of completed
//      games (live vs virtual)
//   2. Format mix — singles vs doubles donut
//   3. Mode mix — live vs virtual donut
//   4. Round distribution — histogram of how many rounds games take
//   5. Notable games — most decisive, closest, highest scoring,
//      longest by round count
//
// When org games scale into the tens of thousands, the heavy
// aggregations here can move to a precomputed orgs_insights row
// (parallel to leaderboard_cache); the read path on this page
// stays the same so the swap is local.

export default function OrgInsights() {
  const { data: games = [], isLoading } = useGames();
  // Org-wide scoring analytics from both-sides games (server-aggregated
  // over game_frames gross). Section hidden until rounds > 0.
  const { data: scoring } = useOrgScoringInsights();
  // Completed games only — in-progress + voided ones lack a final
  // outcome and would pollute the trend math.
  const completed = useMemo(
    () => games.filter((g) => g.status === "complete"),
    [games],
  );

  if (isLoading) {
    return <div className="p-8 text-muted-foreground">Loading…</div>;
  }

  return (
    <div className="px-4 sm:px-8 py-6 sm:py-10 max-w-5xl mx-auto space-y-6">
      <header className="space-y-1">
        <Link
          href="/dashboard"
          className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
        >
          <ChevronLeft size={12} /> Dashboard
        </Link>
        <h1 className="text-2xl sm:text-3xl font-display uppercase tracking-tight flex items-center gap-2">
          <Activity size={22} className="text-primary shrink-0" />
          <span>Insights</span>
        </h1>
        <p className="text-muted-foreground text-sm">
          Org-wide patterns across {completed.length} completed game
          {completed.length === 1 ? "" : "s"}.
        </p>
      </header>

      {completed.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center text-sm text-muted-foreground">
            No completed games yet — play a few and patterns will surface
            here.
          </CardContent>
        </Card>
      ) : (
        <>
          {scoring && scoring.rounds > 0 ? (
            <ScoringInsightsCard data={scoring} />
          ) : null}

          <ActivityTimelineCard games={completed} />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <FormatMixCard games={completed} />
            <ModeMixCard games={completed} />
          </div>

          <RoundDistributionCard games={completed} />

          <NotableGamesCard games={completed} />
        </>
      )}
    </div>
  );
}

// -------------------- Scoring analytics (both-sides) --------------------
// Offense/defense leaders + per-round averages derived from the gross
// each side put up per round. Only rendered when the org has both-
// sides games (data.rounds > 0).

function ScoringInsightsCard({ data }: { data: OrgScoringInsights }) {
  const offense = [...data.players]
    .sort((a, b) => b.pointsScored / b.rounds - a.pointsScored / a.rounds)
    .slice(0, 5);
  const defense = [...data.players]
    .sort((a, b) => a.pointsAllowed / a.rounds - b.pointsAllowed / b.rounds)
    .slice(0, 5);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Target size={14} /> Scoring — both sides
        </CardTitle>
        <CardDescription>
          Gross output per round across {data.rounds} round
          {data.rounds === 1 ? "" : "s"} in {data.games} game
          {data.games === 1 ? "" : "s"}.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="grid grid-cols-3 gap-3">
          <MiniStat
            label="Pts / side / rd"
            value={data.avgSidePerRound.toFixed(1)}
          />
          <MiniStat
            label="Combined / rd"
            value={data.avgCombinedPerRound.toFixed(1)}
          />
          <MiniStat
            label="Cancelled"
            value={`${Math.round(data.cancellationRate * 100)}%`}
          />
        </div>

        {data.players.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <LeaderList
              icon={<Target size={12} />}
              title="Offense"
              subtitle="Points scored / round"
              rows={offense.map((p) => ({
                slug: p.slug,
                name: p.name,
                value: (p.pointsScored / p.rounds).toFixed(1),
              }))}
            />
            <LeaderList
              icon={<Shield size={12} />}
              title="Defense"
              subtitle="Points allowed / round (lower is better)"
              rows={defense.map((p) => ({
                slug: p.slug,
                name: p.name,
                value: (p.pointsAllowed / p.rounds).toFixed(1),
              }))}
            />
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">
            Play a few more both-sides rounds to rank players (min 4 rounds
            each).
          </p>
        )}

        {data.bestRound ? (
          <Link
            href={`/g/${data.bestRound.shareCode}`}
            className="block rounded-md border border-border bg-muted/20 p-3 hover:bg-muted/40 transition-colors"
          >
            <div className="text-[10px] font-display uppercase tracking-wider text-muted-foreground">
              Biggest round
            </div>
            <div className="text-sm mt-0.5">
              <span className="font-display tabular-nums text-primary">
                {data.bestRound.gross}
              </span>{" "}
              by {data.bestRound.players}
              <span className="text-muted-foreground">
                {" "}
                · R{data.bestRound.roundNumber}
              </span>
            </div>
          </Link>
        ) : null}
      </CardContent>
    </Card>
  );
}

function MiniStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-border bg-muted/20 p-3 text-center">
      <div className="text-[10px] font-display uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      <div className="font-display text-2xl tabular-nums mt-1">{value}</div>
    </div>
  );
}

function LeaderList({
  icon,
  title,
  subtitle,
  rows,
}: {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  rows: { slug: string; name: string; value: string }[];
}) {
  return (
    <div>
      <div className="flex items-center gap-1.5 text-xs font-display uppercase tracking-wider text-muted-foreground">
        {icon} {title}
      </div>
      <div className="text-[10px] text-muted-foreground/80 mb-1.5">
        {subtitle}
      </div>
      <ul className="space-y-1">
        {rows.map((r, i) => (
          <li key={r.slug} className="flex items-center gap-2 text-sm">
            <span className="w-4 text-xs text-muted-foreground tabular-nums">
              {i + 1}
            </span>
            <Link
              href={`/dashboard/players/${r.slug}`}
              className="flex-1 truncate hover:underline"
            >
              {r.name}
            </Link>
            <span className="font-display tabular-nums">{r.value}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// -------------------- Activity timeline --------------------
// Weekly bucket of games over the most recent 12 weeks. Two
// stacked segments per bar (live + virtual) so the casual-vs-
// virtual mix reads at a glance.

function ActivityTimelineCard({ games }: { games: Game[] }) {
  const data = useMemo(() => weeklyActivity(games, 12), [games]);
  const maxN = Math.max(1, ...data.map((d) => d.total));

  const W = 600;
  const H = 160;
  const PAD = { top: 12, right: 8, bottom: 22, left: 28 };
  const innerW = W - PAD.left - PAD.right;
  const innerH = H - PAD.top - PAD.bottom;
  const gap = 4;
  const barW = Math.max(4, innerW / data.length - gap);
  const x = (i: number) =>
    PAD.left + (i + 0.5) * (innerW / data.length) - barW / 2;
  const y = (n: number) => PAD.top + innerH - (n / maxN) * innerH;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Activity size={14} /> Weekly activity
        </CardTitle>
        <CardDescription>
          Completed games per week, last 12 weeks. Live games stack below
          virtual.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <svg
          viewBox={`0 0 ${W} ${H}`}
          preserveAspectRatio="xMidYMid meet"
          className="w-full h-auto"
          role="img"
          aria-label="Games played per week"
        >
          {/* Y axis tick: max */}
          <line
            x1={PAD.left}
            x2={W - PAD.right}
            y1={y(maxN)}
            y2={y(maxN)}
            className="stroke-current text-muted-foreground/20"
            strokeWidth="0.5"
          />
          <text
            x={PAD.left - 4}
            y={y(maxN) + 3}
            textAnchor="end"
            className="fill-current text-muted-foreground text-[9px] font-display"
          >
            {maxN}
          </text>
          <text
            x={PAD.left - 4}
            y={y(0) + 3}
            textAnchor="end"
            className="fill-current text-muted-foreground text-[9px] font-display"
          >
            0
          </text>
          {/* Bars */}
          {data.map((d, i) => {
            const liveH = (d.live / maxN) * innerH;
            const virtH = (d.virtual / maxN) * innerH;
            const liveY = y(0) - liveH;
            const virtY = liveY - virtH;
            return (
              <g key={d.weekStart}>
                {d.live > 0 ? (
                  <rect
                    x={x(i)}
                    y={liveY}
                    width={barW}
                    height={liveH}
                    className="fill-primary"
                    opacity={0.85}
                  />
                ) : null}
                {d.virtual > 0 ? (
                  <rect
                    x={x(i)}
                    y={virtY}
                    width={barW}
                    height={virtH}
                    className="fill-primary"
                    opacity={0.35}
                  />
                ) : null}
                {/* Week label every 3 bars + last */}
                {i % 3 === 0 || i === data.length - 1 ? (
                  <text
                    x={x(i) + barW / 2}
                    y={H - 6}
                    textAnchor="middle"
                    className="fill-current text-muted-foreground text-[9px] font-display"
                  >
                    {d.shortLabel}
                  </text>
                ) : null}
              </g>
            );
          })}
        </svg>
        <div className="mt-2 flex items-center gap-4 text-[11px]">
          <span className="inline-flex items-center gap-1.5">
            <span className="inline-block w-3 h-2 bg-primary rounded-sm" />
            <span className="text-muted-foreground">Live</span>
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="inline-block w-3 h-2 bg-primary/40 rounded-sm" />
            <span className="text-muted-foreground">Virtual</span>
          </span>
        </div>
      </CardContent>
    </Card>
  );
}

type WeekBucket = {
  weekStart: string; // YYYY-MM-DD of Sunday/Monday-of-week
  shortLabel: string; // e.g. "May 21"
  total: number;
  live: number;
  virtual: number;
};

function weeklyActivity(games: Game[], weeks: number): WeekBucket[] {
  // Build N weeks ending today, each spanning Mon-Sun. Use UTC math
  // throughout so a midnight playedOn doesn't drift across a TZ
  // boundary and land in the wrong bucket.
  const buckets: WeekBucket[] = [];
  const today = new Date();
  today.setUTCHours(0, 0, 0, 0);
  // Anchor on Monday-of-this-week (UTC).
  const dow = (today.getUTCDay() + 6) % 7; // 0=Mon, 6=Sun
  today.setUTCDate(today.getUTCDate() - dow);
  for (let i = weeks - 1; i >= 0; i--) {
    const start = new Date(today);
    start.setUTCDate(start.getUTCDate() - i * 7);
    const startStr = start.toISOString().slice(0, 10);
    const shortLabel = start.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
    });
    buckets.push({
      weekStart: startStr,
      shortLabel,
      total: 0,
      live: 0,
      virtual: 0,
    });
  }
  for (const g of games) {
    // Find which bucket this game falls in by comparing playedOn
    // to each bucket's start (lex YYYY-MM-DD compare works).
    for (let i = buckets.length - 1; i >= 0; i--) {
      if (g.playedOn >= buckets[i]!.weekStart) {
        buckets[i]!.total += 1;
        if (g.isVirtual) buckets[i]!.virtual += 1;
        else buckets[i]!.live += 1;
        break;
      }
    }
  }
  return buckets;
}

// -------------------- Format / mode donuts --------------------
// Two donut charts for at-a-glance composition. SVG arcs, no chart
// lib. Each donut renders the share + the absolute count.

function FormatMixCard({ games }: { games: Game[] }) {
  const singles = games.filter((g) => g.format === "singles").length;
  const doubles = games.filter((g) => g.format === "doubles").length;
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Trophy size={14} /> Singles vs doubles
        </CardTitle>
        <CardDescription>How the org plays.</CardDescription>
      </CardHeader>
      <CardContent>
        <DonutWithLegend
          a={{ label: "Singles", value: singles, className: "text-primary" }}
          b={{
            label: "Doubles",
            value: doubles,
            className: "text-muted-foreground",
          }}
        />
      </CardContent>
    </Card>
  );
}

function ModeMixCard({ games }: { games: Game[] }) {
  const live = games.filter((g) => !g.isVirtual).length;
  const virtual = games.filter((g) => g.isVirtual).length;
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Boxes size={14} /> Live vs virtual
        </CardTitle>
        <CardDescription>Backyard scoreboard vs 3D play.</CardDescription>
      </CardHeader>
      <CardContent>
        <DonutWithLegend
          a={{ label: "Live", value: live, className: "text-primary" }}
          b={{
            label: "Virtual",
            value: virtual,
            className: "text-muted-foreground",
          }}
        />
      </CardContent>
    </Card>
  );
}

function DonutWithLegend({
  a,
  b,
}: {
  a: { label: string; value: number; className: string };
  b: { label: string; value: number; className: string };
}) {
  const total = a.value + b.value;
  const aFrac = total === 0 ? 0 : a.value / total;
  // Donut geometry: stroke-dasharray on a circle of circumference
  // 2πr. We use r=24 (circumference ≈ 150.8) and convert fractions
  // to dash lengths. Background circle is the muted track; the
  // foreground arc draws the share.
  const r = 24;
  const C = 2 * Math.PI * r;
  const dash = aFrac * C;
  return (
    <div className="flex items-center gap-4">
      <svg
        viewBox="0 0 60 60"
        className="w-20 h-20 shrink-0"
        role="img"
        aria-label={`${a.label} ${Math.round(aFrac * 100)} percent`}
      >
        <circle
          cx="30"
          cy="30"
          r={r}
          className="stroke-current text-muted-foreground/30"
          strokeWidth="10"
          fill="none"
        />
        <circle
          cx="30"
          cy="30"
          r={r}
          className={`stroke-current ${a.className}`}
          strokeWidth="10"
          fill="none"
          strokeDasharray={`${dash} ${C - dash}`}
          strokeDashoffset={C / 4 /* start at top */}
          transform="rotate(-90 30 30)"
        />
      </svg>
      <div className="flex-1 space-y-1 text-sm">
        <div className="flex items-center justify-between gap-2">
          <span className="inline-flex items-center gap-1.5">
            <span
              className={`inline-block w-3 h-3 rounded-sm bg-current ${a.className}`}
            />
            <span>{a.label}</span>
          </span>
          <span className="tabular-nums text-muted-foreground">
            {a.value} · {total === 0 ? "0" : Math.round(aFrac * 100)}%
          </span>
        </div>
        <div className="flex items-center justify-between gap-2">
          <span className="inline-flex items-center gap-1.5">
            <span className="inline-block w-3 h-3 rounded-sm bg-muted-foreground/40" />
            <span>{b.label}</span>
          </span>
          <span className="tabular-nums text-muted-foreground">
            {b.value} ·{" "}
            {total === 0 ? "0" : Math.round((1 - aFrac) * 100)}%
          </span>
        </div>
      </div>
    </div>
  );
}

// -------------------- Round distribution --------------------
// Histogram of games-by-round-count. Cornhole-to-21 games can run
// anywhere from ~3 rounds (a 7-point round to win quickly) up to
// 15+ on a slow back-and-forth. Buckets capped at 1..15 with a
// "16+" bucket so outlier marathons don't squish the rest.

function RoundDistributionCard({ games }: { games: Game[] }) {
  const dist = useMemo(() => {
    const map = new Map<number | "16+", number>();
    for (let i = 1; i <= 15; i++) map.set(i, 0);
    map.set("16+", 0);
    for (const g of games) {
      const r = g.currentRound;
      if (r <= 0) continue;
      const key = r <= 15 ? r : "16+";
      map.set(key, (map.get(key) ?? 0) + 1);
    }
    return Array.from(map.entries()) as [number | "16+", number][];
  }, [games]);
  const maxN = Math.max(1, ...dist.map(([, n]) => n));
  const median = useMemo(() => {
    const sorted = games
      .map((g) => g.currentRound)
      .filter((r) => r > 0)
      .sort((a, b) => a - b);
    if (sorted.length === 0) return null;
    const mid = Math.floor(sorted.length / 2);
    return sorted.length % 2 === 0
      ? (sorted[mid - 1]! + sorted[mid]!) / 2
      : sorted[mid]!;
  }, [games]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Round count distribution</CardTitle>
        <CardDescription>
          How many rounds games tend to last.{" "}
          {median != null ? `Median: ${median} rounds.` : ""}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex items-end gap-1 h-32">
          {dist.map(([key, n]) => {
            const h = (n / maxN) * 100;
            return (
              <div
                key={String(key)}
                className="flex-1 flex flex-col items-center gap-1"
                title={`${n} game${n === 1 ? "" : "s"} ended in ${key} rounds`}
              >
                <div className="w-full flex flex-col-reverse h-full">
                  <div
                    className="w-full bg-primary rounded-t"
                    style={{ height: `${h}%`, minHeight: n > 0 ? 2 : 0 }}
                  />
                </div>
                <div className="text-[9px] font-display text-muted-foreground">
                  {key}
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

// -------------------- Notable games --------------------
// Four "wow factor" games surfaced from the dataset — each links
// into the game detail page so users can drill in. Compute once
// per render via useMemo.

function NotableGamesCard({ games }: { games: Game[] }) {
  const notables = useMemo(() => {
    if (games.length === 0) return null;
    function margin(g: Game) {
      return Math.abs(g.team1Score - g.team2Score);
    }
    function totalPoints(g: Game) {
      return g.team1Score + g.team2Score;
    }
    const mostDecisive = games
      .slice()
      .sort((a, b) => margin(b) - margin(a))[0]!;
    const closest = games
      .slice()
      .sort((a, b) => margin(a) - margin(b))[0]!;
    const highest = games
      .slice()
      .sort((a, b) => totalPoints(b) - totalPoints(a))[0]!;
    const longest = games
      .slice()
      .sort((a, b) => b.currentRound - a.currentRound)[0]!;
    return { mostDecisive, closest, highest, longest };
  }, [games]);

  if (!notables) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Play size={14} /> Notable games
        </CardTitle>
        <CardDescription>
          Edge cases from your history. Click any to view round-by-round.
        </CardDescription>
      </CardHeader>
      <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <NotableTile
          label="Most decisive"
          subtitle={`${Math.abs(notables.mostDecisive.team1Score - notables.mostDecisive.team2Score)}-point margin`}
          game={notables.mostDecisive}
        />
        <NotableTile
          label="Closest"
          subtitle={`${Math.abs(notables.closest.team1Score - notables.closest.team2Score)}-point margin`}
          game={notables.closest}
        />
        <NotableTile
          label="Highest scoring"
          subtitle={`${notables.highest.team1Score + notables.highest.team2Score} combined`}
          game={notables.highest}
        />
        <NotableTile
          label="Longest"
          subtitle={`${notables.longest.currentRound} rounds`}
          game={notables.longest}
        />
      </CardContent>
    </Card>
  );
}

function NotableTile({
  label,
  subtitle,
  game,
}: {
  label: string;
  subtitle: string;
  game: Game;
}) {
  return (
    <Link
      href={`/dashboard/games/${game.id}`}
      className="block rounded-md border border-border bg-muted/10 p-3 hover:bg-muted/30 transition-colors"
    >
      <div className="text-[10px] font-display uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      <div className="font-display text-lg tabular-nums">
        {game.team1Score}–{game.team2Score}
      </div>
      <div className="text-xs text-muted-foreground truncate">
        {game.team1Players.map((p) => p.name).join(" & ")} vs{" "}
        {game.team2Players.map((p) => p.name).join(" & ")}
      </div>
      <div className="text-[10px] text-muted-foreground/80 mt-1">
        {subtitle} · {new Date(game.playedOn).toLocaleDateString()}
      </div>
    </Link>
  );
}
